// ---------------------------------------------------------------------------
// spell_classifier.ts -- Topology-first spell classification
//
// Multi-stroke aware: templates match primarily on stroke COUNT and
// STRUCTURE (lasso, line, dot, cross, etc.) rather than raw geometry.
// Geometry serves as confirmation / tiebreaker, not the primary signal.
//
// Every registered spell gesture competes simultaneously.  The template
// with the highest composite score wins.
//
// Design: pure functions, no external state.  Hit-testing is injected as a
// callback so the classifier stays independent of any state store.
// ---------------------------------------------------------------------------

import type { StrokePoint, BlockId } from '../shared/notebook_model.js';
import { extractFeatures, type StrokeFeatures, type LassoData } from './stroke_geometry.js';

// -- Public types -------------------------------------------------------------

export interface ClassificationResult {
  spellId: string;
  confidence: number;
  parameters: Record<string, unknown>;
}

export type BlockHitTest = (x: number, y: number) => { id: BlockId; kind: string } | null;

// -- Soft gates ---------------------------------------------------------------

function gate(x: number, lo: number, hi: number): number {
  if (hi <= lo) return x >= lo ? 1 : 0;
  return Math.max(0, Math.min(1, (x - lo) / (hi - lo)));
}

function gateInv(x: number, lo: number, hi: number): number {
  return 1 - gate(x, lo, hi);
}

function bump(x: number, center: number, sigma: number): number {
  const d = (x - center) / sigma;
  return Math.exp(-0.5 * d * d);
}

function angleBump(a: number, target: number, sigma: number): number {
  let d = a - target;
  while (d >  Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  return Math.exp(-0.5 * (d / sigma) * (d / sigma));
}

// -- Per-stroke summary -------------------------------------------------------

interface StrokeSummary {
  feat: StrokeFeatures;
  isLasso: boolean;
  lassoBbox: { x: number; y: number; width: number; height: number } | null;
  isLine: boolean;       // straightness > 0.6
  isUpward: boolean;     // netDirection close to -PI/2
  isDownward: boolean;   // netDirection close to PI/2
  isShort: boolean;      // arcLength < 80
  hasCrossing: boolean;  // selfCrossings >= 1
  centroid: { x: number; y: number };
}

function summarize(pts: StrokePoint[]): StrokeSummary {
  const feat = extractFeatures(pts);
  const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
  const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;

  return {
    feat,
    isLasso: feat.lasso.isClosed && feat.lasso.area >= 300,
    lassoBbox: feat.lasso.bounds?.bbox ?? null,
    isLine: feat.straightness > 0.6,
    isUpward: angleBump(feat.netDirection, -Math.PI / 2, 0.65) > 0.5,
    isDownward: angleBump(feat.netDirection, Math.PI / 2, 0.65) > 0.5,
    isShort: feat.arcLength < 80,
    hasCrossing: feat.selfCrossings >= 1,
    centroid: { x: cx, y: cy },
  };
}

/** Check if point is inside a bbox (with padding). */
function insideBbox(
  x: number, y: number,
  bb: { x: number; y: number; width: number; height: number },
  pad = 0,
): boolean {
  return x >= bb.x - pad && x <= bb.x + bb.width + pad &&
         y >= bb.y - pad && y <= bb.y + bb.height + pad;
}

/** Check if a stroke's centroid is inside a bbox. */
function strokeInsideBbox(
  s: StrokeSummary,
  bb: { x: number; y: number; width: number; height: number },
  pad = 15,
): boolean {
  return insideBbox(s.centroid.x, s.centroid.y, bb, pad);
}

/** Check if two strokes cross each other (any segment intersection). */
function strokesCross(a: StrokePoint[], b: StrokePoint[]): boolean {
  // Downsample for performance
  const step = Math.max(1, Math.floor(Math.max(a.length, b.length) / 20));
  for (let i = step; i < a.length; i += step) {
    for (let j = step; j < b.length; j += step) {
      if (segIntersect(
        a[i - step].x, a[i - step].y, a[i].x, a[i].y,
        b[j - step].x, b[j - step].y, b[j].x, b[j].y,
      )) return true;
    }
  }
  return false;
}

function segIntersect(
  ax: number, ay: number, bx: number, by: number,
  cx: number, cy: number, dx: number, dy: number,
): boolean {
  const d1 = (dx - cx) * (ay - cy) - (dy - cy) * (ax - cx);
  const d2 = (dx - cx) * (by - cy) - (dy - cy) * (bx - cx);
  const d3 = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
  const d4 = (bx - ax) * (dy - ay) - (by - ay) * (dx - ax);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
         ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

/**
 * Check if a stroke is a check mark / tick: a continuous angled line that
 * goes down-right then sharply up-right (V shape).
 * Requires a clear bend with angle between 30° and 120°.
 */
function isCheckMark(pts: StrokePoint[]): boolean {
  if (pts.length < 8) return false;

  // Find the lowest point (the bend vertex)
  let lowestIdx = 0;
  for (let i = 1; i < pts.length; i++) {
    if (pts[i].y > pts[lowestIdx].y) lowestIdx = i;
  }
  // Bend should be roughly in the middle third
  const third = pts.length / 3;
  if (lowestIdx < third * 0.5 || lowestIdx > third * 2.5) return false;

  // Vector in (start → bend) and (bend → end)
  const inDx = pts[lowestIdx].x - pts[0].x;
  const inDy = pts[lowestIdx].y - pts[0].y;
  const outDx = pts[pts.length - 1].x - pts[lowestIdx].x;
  const outDy = pts[pts.length - 1].y - pts[lowestIdx].y;

  // First leg should go downward (dy > 0), second leg upward (dy < 0)
  if (inDy < 5 || outDy > -5) return false;

  // Angle between legs (at the bend)
  const dot = inDx * outDx + inDy * outDy;
  const mag = Math.sqrt(inDx * inDx + inDy * inDy) * Math.sqrt(outDx * outDx + outDy * outDy);
  if (mag < 1) return false;
  const angle = Math.acos(Math.max(-1, Math.min(1, dot / mag)));
  // Accept 30° to 130° bend
  return angle > Math.PI / 6 && angle < Math.PI * 13 / 18;
}

/** Check if a stroke is roughly closed (box-like). */
function isClosedShape(pts: StrokePoint[]): boolean {
  if (pts.length < 8) return false;
  const dx = pts[pts.length - 1].x - pts[0].x;
  const dy = pts[pts.length - 1].y - pts[0].y;
  const closeDist = Math.sqrt(dx * dx + dy * dy);
  let arcLen = 0;
  for (let i = 1; i < pts.length; i++) {
    const sx = pts[i].x - pts[i - 1].x;
    const sy = pts[i].y - pts[i - 1].y;
    arcLen += Math.sqrt(sx * sx + sy * sy);
  }
  return closeDist < Math.max(20, arcLen * 0.25) && arcLen > 30;
}

// -- Multi-stroke gesture template type ---------------------------------------

interface MultiGestureMatch {
  confidence: number;
  parameters: Record<string, unknown>;
}

type MultiGestureMatcher = (
  strokes: StrokeSummary[],
  rawStrokes: StrokePoint[][],
  hitTest: BlockHitTest,
  pageId: string,
) => MultiGestureMatch;

interface MultiGestureTemplate {
  id: string;
  match: MultiGestureMatcher;
}

// -- Entry points -------------------------------------------------------------

/**
 * Multi-stroke classification: each stroke is analysed independently, then
 * templates match on stroke topology (count, order, relationships).
 */
export function classifySpellFromStrokes(
  strokes: StrokePoint[][],
  hitTest: BlockHitTest,
  currentPageId: string,
): ClassificationResult {
  // Filter out empty strokes
  const valid = strokes.filter(s => s.length >= 2);
  if (valid.length === 0) {
    return { spellId: 'select.point', confidence: 0.4, parameters: { pageId: currentPageId } };
  }

  const summaries = valid.map(s => summarize(s));
  return pickBest(summaries, valid, hitTest, currentPageId);
}

/** Backward-compat wrapper: single flat array treated as one stroke. */
export function classifySpellFromStroke(
  fullPoints: StrokePoint[],
  hitTest: BlockHitTest,
  currentPageId: string,
): ClassificationResult {
  if (fullPoints.length < 2) {
    return { spellId: 'select.point', confidence: 0.4, parameters: { pageId: currentPageId } };
  }
  return classifySpellFromStrokes([fullPoints], hitTest, currentPageId);
}

// -- Core dispatch ------------------------------------------------------------

function pickBest(
  summaries: StrokeSummary[],
  rawStrokes: StrokePoint[][],
  hitTest: BlockHitTest,
  pageId: string,
): ClassificationResult {
  let bestId   = 'select.point';
  let bestConf = 0;
  let bestParams: Record<string, unknown> = { pageId };

  for (const tpl of TEMPLATES) {
    const m = tpl.match(summaries, rawStrokes, hitTest, pageId);
    if (m.confidence > bestConf) {
      bestId     = tpl.id;
      bestConf   = m.confidence;
      bestParams = m.parameters;
    }
  }

  return { spellId: bestId, confidence: bestConf, parameters: bestParams };
}

// -- Gesture templates --------------------------------------------------------
//
// TOPOLOGY FIRST: templates gate on stroke count, then check per-stroke roles.
// Geometry only disambiguates within a topology class.

const TEMPLATES: MultiGestureTemplate[] = [

  // --------------------------------------------------------------------------
  // select.point -- 1 stroke, very short
  // --------------------------------------------------------------------------
  {
    id: 'select.point',
    match(strokes, raw, hitTest, pageId) {
      if (strokes.length !== 1) return { confidence: 0, parameters: { pageId } };
      const s = strokes[0];
      const short = gateInv(s.feat.arcLength, 40, 100);
      const hit = hitTest(s.feat.endPoint.x, s.feat.endPoint.y);
      return {
        confidence: 0.88 * short * (hit ? 1 : 0.4),
        parameters: { pageId, target: hit?.id ?? null },
      };
    },
  },

  // --------------------------------------------------------------------------
  // select.region -- 1 stroke, closed lasso, NOT directional
  // --------------------------------------------------------------------------
  {
    id: 'select.region',
    match(strokes, raw, hitTest, pageId) {
      if (strokes.length !== 1) return { confidence: 0, parameters: { pageId } };
      const s = strokes[0];
      if (!s.isLasso) return { confidence: 0, parameters: { pageId } };
      // Require minimum arc length so short wobbly strokes don't trigger
      if (s.feat.arcLength < 120) return { confidence: 0, parameters: { pageId } };
      // Reject clearly directional strokes (arrows) — a lasso should have
      // low displacement relative to arc length (user draws a loop, not a line)
      if (s.feat.straightness > 0.4) return { confidence: 0, parameters: { pageId } };
      // Also reject clearly upward/downward strokes (likely arrow gestures)
      if (s.isUpward || s.isDownward) return { confidence: 0, parameters: { pageId } };
      return {
        confidence: 0.90,
        parameters: { pageId, region: s.feat.lasso.bounds },
      };
    },
  },

  // --------------------------------------------------------------------------
  // erase.region -- 2 strokes: (1) lasso, (2) slash through center
  // --------------------------------------------------------------------------
  {
    id: 'erase.region',
    match(strokes, raw, hitTest, pageId) {
      if (strokes.length !== 2) return { confidence: 0, parameters: { pageId } };
      const [s1, s2] = strokes;
      if (!s1.isLasso) return { confidence: 0, parameters: { pageId } };

      // Stroke 2 must be a line that passes through the lasso interior
      if (!s2.isLine) return { confidence: 0, parameters: { pageId } };
      const bb = s1.lassoBbox!;
      if (!strokeInsideBbox(s2, bb)) return { confidence: 0, parameters: { pageId } };

      return {
        confidence: 0.92,
        parameters: { pageId, region: s1.feat.lasso.bounds },
      };
    },
  },

  // --------------------------------------------------------------------------
  // erase.line -- 1 stroke, very straight, not a lasso
  // --------------------------------------------------------------------------
  {
    id: 'erase.line',
    match(strokes, raw, hitTest, pageId) {
      if (strokes.length !== 1) return { confidence: 0, parameters: { pageId } };
      const s = strokes[0];
      const straight = gate(s.feat.straightness, 0.70, 0.88);
      const reach    = gate(s.feat.disp, 20, 50);
      const noLasso  = s.isLasso ? 0 : 1;
      const lowWind  = gateInv(s.feat.turn.absWinding, 0.15, 0.35);

      const score = straight * reach * noLasso * lowWind;
      if (score < 0.15) return { confidence: 0, parameters: { pageId } };

      return {
        confidence: 0.84 * score,
        parameters: {
          pageId,
          lineStart: { x: s.feat.startPoint.x, y: s.feat.startPoint.y },
          lineEnd:   { x: s.feat.endPoint.x, y: s.feat.endPoint.y },
          lineBbox:  s.feat.bbox,
        },
      };
    },
  },

  // --------------------------------------------------------------------------
  // derive.step -- 2 strokes: (1) lasso, (2) downward tail
  // --------------------------------------------------------------------------
  {
    id: 'derive.step',
    match(strokes, raw, hitTest, pageId) {
      if (strokes.length !== 2) return { confidence: 0, parameters: { pageId } };
      const [s1, s2] = strokes;
      if (!s1.isLasso) return { confidence: 0, parameters: { pageId } };
      if (!s2.isDownward) return { confidence: 0, parameters: { pageId } };

      const hitCenter = hitTest(
        s1.lassoBbox!.x + s1.lassoBbox!.width / 2,
        s1.lassoBbox!.y + s1.lassoBbox!.height / 2,
      );

      return {
        confidence: 0.87,
        parameters: {
          pageId,
          sourceRegion: s1.feat.lasso.bounds,
          sourceBlockId: hitCenter?.id ?? null,
        },
      };
    },
  },

  // --------------------------------------------------------------------------
  // annotate.link -- 1 stroke, straight, connects two distinct blocks
  // --------------------------------------------------------------------------
  {
    id: 'annotate.link',
    match(strokes, raw, hitTest, pageId) {
      if (strokes.length !== 1) return { confidence: 0, parameters: { pageId } };
      const s = strokes[0];
      const straight = gate(s.feat.straightness, 0.45, 0.65);
      const reach    = gate(s.feat.disp, 40, 80);
      const noLasso  = s.isLasso ? 0 : 1;

      if (straight * reach * noLasso < 0.1) return { confidence: 0, parameters: { pageId } };

      const hitFrom = hitTest(s.feat.startPoint.x, s.feat.startPoint.y);
      const hitTo   = hitTest(s.feat.endPoint.x, s.feat.endPoint.y);

      if (hitFrom && hitTo && hitFrom.id !== hitTo.id) {
        return {
          confidence: 0.87 * straight * reach,
          parameters: { pageId, fromBlockId: hitFrom.id, toBlockId: hitTo.id },
        };
      }

      return { confidence: 0, parameters: { pageId } };
    },
  },

  // --------------------------------------------------------------------------
  // block.duplicate -- 1 stroke, figure-8 over a block
  // --------------------------------------------------------------------------
  {
    id: 'block.duplicate',
    match(strokes, raw, hitTest, pageId) {
      if (strokes.length !== 1) return { confidence: 0, parameters: { pageId } };
      const s = strokes[0];
      const hasCross  = gate(s.feat.selfCrossings, 0.5, 1.5);
      const zeroWind  = bump(s.feat.turn.absWinding, 0, 0.4);
      const enough    = gate(s.feat.arcLength, 60, 120);

      const score = hasCross * zeroWind * enough;
      if (score < 0.1) return { confidence: 0, parameters: { pageId } };

      const hitCenter = hitTest(
        s.feat.bbox.x + s.feat.bbox.width / 2,
        s.feat.bbox.y + s.feat.bbox.height / 2,
      );

      if (hitCenter) {
        return {
          confidence: 0.82 * score,
          parameters: { pageId, sourceBlockId: hitCenter.id, sourceBbox: hitCenter },
        };
      }
      return { confidence: 0, parameters: { pageId } };
    },
  },

  // --------------------------------------------------------------------------
  // arcane.open -- 1 stroke, expanding spiral
  // --------------------------------------------------------------------------
  {
    id: 'arcane.open',
    match(strokes, raw, hitTest, pageId) {
      if (strokes.length !== 1) return { confidence: 0, parameters: { pageId } };
      const s = strokes[0];
      const multiWind = gate(s.feat.turn.absWinding, 1.2, 2.0);
      const mono      = gate(s.feat.turn.monotonicRatio, 0.6, 0.8);
      const lowCross  = gateInv(s.feat.selfCrossings, 1, 3);

      const score = multiWind * mono * lowCross;
      if (score < 0.1) return { confidence: 0, parameters: { pageId } };

      return {
        confidence: 0.75 * score,
        parameters: { pageId },
      };
    },
  },

  // --------------------------------------------------------------------------
  // layer.up -- 1 short upward line on a block, OR 2 strokes: line + chevron
  // --------------------------------------------------------------------------
  {
    id: 'layer.up',
    match(strokes, raw, hitTest, pageId) {
      if (strokes.length === 1) {
        const s = strokes[0];
        if (!s.isUpward || s.isLasso) return { confidence: 0, parameters: { pageId } };
        // Must be line-like, not a loop
        const straight = gate(s.feat.straightness, 0.25, 0.50);
        const reach = gate(s.feat.disp, 8, 30);
        const notTooLong = gateInv(s.feat.disp, 150, 250);
        const hit = hitTest(s.feat.startPoint.x, s.feat.startPoint.y);
        if (!hit) return { confidence: 0, parameters: { pageId } };
        return {
          confidence: 0.92 * reach * notTooLong * straight,
          parameters: { pageId, blockId: hit.id },
        };
      }
      if (strokes.length === 2) {
        const [s1, s2] = strokes;
        if (!s1.isUpward || s1.isLasso) return { confidence: 0, parameters: { pageId } };
        if (!s2.isShort) return { confidence: 0, parameters: { pageId } };
        const s1End = s1.feat.endPoint;
        const chevDist = Math.hypot(s2.centroid.x - s1End.x, s2.centroid.y - s1End.y);
        const nearTip = gateInv(chevDist, 15, 50);
        const hit = hitTest(s1.feat.startPoint.x, s1.feat.startPoint.y);
        return {
          confidence: 0.94 * nearTip * (hit ? 1 : 0.5),
          parameters: { pageId, blockId: hit?.id ?? null },
        };
      }
      return { confidence: 0, parameters: { pageId } };
    },
  },

  // --------------------------------------------------------------------------
  // layer.down -- 1 short downward line on a block, OR 2 strokes: line + chevron
  // --------------------------------------------------------------------------
  {
    id: 'layer.down',
    match(strokes, raw, hitTest, pageId) {
      if (strokes.length === 1) {
        const s = strokes[0];
        if (!s.isDownward || s.isLasso) return { confidence: 0, parameters: { pageId } };
        const straight = gate(s.feat.straightness, 0.25, 0.50);
        const reach = gate(s.feat.disp, 8, 30);
        const notTooLong = gateInv(s.feat.disp, 150, 250);
        const hit = hitTest(s.feat.startPoint.x, s.feat.startPoint.y);
        if (!hit) return { confidence: 0, parameters: { pageId } };
        return {
          confidence: 0.92 * reach * notTooLong * straight,
          parameters: { pageId, blockId: hit.id },
        };
      }
      if (strokes.length === 2) {
        const [s1, s2] = strokes;
        if (!s1.isDownward || s1.isLasso) return { confidence: 0, parameters: { pageId } };
        if (!s2.isShort) return { confidence: 0, parameters: { pageId } };
        const s1End = s1.feat.endPoint;
        const chevDist = Math.hypot(s2.centroid.x - s1End.x, s2.centroid.y - s1End.y);
        const nearTip = gateInv(chevDist, 15, 50);
        const hit = hitTest(s1.feat.startPoint.x, s1.feat.startPoint.y);
        return {
          confidence: 0.94 * nearTip * (hit ? 1 : 0.5),
          parameters: { pageId, blockId: hit?.id ?? null },
        };
      }
      return { confidence: 0, parameters: { pageId } };
    },
  },

  // --------------------------------------------------------------------------
  // paste.here -- 2 strokes: two crossing lines of similar length forming
  //               an X shape, crossing near both midpoints, not on a block
  // --------------------------------------------------------------------------
  {
    id: 'paste.here',
    match(strokes, raw, hitTest, pageId) {
      if (strokes.length !== 2) return { confidence: 0, parameters: { pageId } };
      const [s1, s2] = strokes;

      // Both strokes should be line-like
      if (!s1.isLine || !s2.isLine) return { confidence: 0, parameters: { pageId } };

      // Similar length: shorter/longer ratio must be > 0.5
      const lenRatio = Math.min(s1.feat.arcLength, s2.feat.arcLength)
                     / Math.max(s1.feat.arcLength, s2.feat.arcLength);
      if (lenRatio < 0.5) return { confidence: 0, parameters: { pageId } };

      // Both must be reasonably long (not accidental taps)
      if (s1.feat.arcLength < 30 || s2.feat.arcLength < 30)
        return { confidence: 0, parameters: { pageId } };

      // They must cross each other
      if (!strokesCross(raw[0], raw[1])) return { confidence: 0, parameters: { pageId } };

      // Crossing should be near both midpoints
      const mid1 = raw[0][Math.floor(raw[0].length / 2)];
      const mid2 = raw[1][Math.floor(raw[1].length / 2)];
      const midDist = Math.hypot(mid1.x - mid2.x, mid1.y - mid2.y);
      const avgLen = (s1.feat.arcLength + s2.feat.arcLength) / 2;
      if (midDist > avgLen * 0.4) return { confidence: 0, parameters: { pageId } };

      // Center of the X
      const cx = (s1.centroid.x + s2.centroid.x) / 2;
      const cy = (s1.centroid.y + s2.centroid.y) / 2;

      // Should NOT be on a block
      const hitCenter = hitTest(cx, cy);
      if (hitCenter) return { confidence: 0, parameters: { pageId } };

      const lenScore = bump(lenRatio, 1, 0.4);
      return {
        confidence: 0.90 * lenScore,
        parameters: { pageId, targetX: cx, targetY: cy },
      };
    },
  },

  // --------------------------------------------------------------------------
  // copy.region -- 2 strokes: (1) lasso, (2) check mark (angled continuous line)
  // Works for both blocks and free-form regions.
  // --------------------------------------------------------------------------
  {
    id: 'copy.region',
    match(strokes, raw, hitTest, pageId) {
      if (strokes.length !== 2) return { confidence: 0, parameters: { pageId } };
      const [s1, s2] = strokes;
      if (!s1.isLasso) return { confidence: 0, parameters: { pageId } };

      // Stroke 2 must be inside the lasso and be a check mark
      const bb = s1.lassoBbox!;
      if (!strokeInsideBbox(s2, bb)) return { confidence: 0, parameters: { pageId } };
      if (!isCheckMark(raw[1])) return { confidence: 0, parameters: { pageId } };

      return {
        confidence: 0.93,
        parameters: { pageId, region: s1.feat.lasso.bounds },
      };
    },
  },

  // --------------------------------------------------------------------------
  // snapshot.region -- 2 strokes: (1) lasso, (2) closed shape (box) inside
  // --------------------------------------------------------------------------
  {
    id: 'snapshot.region',
    match(strokes, raw, hitTest, pageId) {
      if (strokes.length !== 2) return { confidence: 0, parameters: { pageId } };
      const [s1, s2] = strokes;
      if (!s1.isLasso) return { confidence: 0, parameters: { pageId } };

      const bb = s1.lassoBbox!;
      if (!strokeInsideBbox(s2, bb)) return { confidence: 0, parameters: { pageId } };
      if (!isClosedShape(raw[1])) return { confidence: 0, parameters: { pageId } };

      return {
        confidence: 0.91,
        parameters: { pageId, region: s1.feat.lasso.bounds },
      };
    },
  },

  // --------------------------------------------------------------------------
  // arcane.eval -- 2+ strokes: (1) lasso around empty space, (2+) rune inside
  // Falls back from other 2-stroke lasso spells when inner shape is unrecognized.
  // --------------------------------------------------------------------------
  {
    id: 'arcane.eval',
    match(strokes, raw, hitTest, pageId) {
      if (strokes.length < 2) return { confidence: 0, parameters: { pageId } };
      const s1 = strokes[0];
      if (!s1.isLasso) return { confidence: 0, parameters: { pageId } };

      const bb = s1.lassoBbox!;

      // At least one inner stroke must be inside the lasso
      let hasInner = false;
      for (let i = 1; i < strokes.length; i++) {
        if (strokeInsideBbox(strokes[i], bb)) { hasInner = true; break; }
      }
      if (!hasInner) return { confidence: 0, parameters: { pageId } };

      // No blocks inside the lasso -- arcane eval is for empty space + rune
      const centerHit = hitTest(bb.x + bb.width / 2, bb.y + bb.height / 2);
      if (centerHit) return { confidence: 0, parameters: { pageId } };

      return {
        confidence: 0.85,
        parameters: { pageId, region: s1.feat.lasso.bounds },
      };
    },
  },
];